import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';

import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';

import Form, { FormTextField, FormSwitch, FormSelect } from '@ts-core/core/components/FormV2';

import { CourseFormInput } from '../../types/form';
import { Course } from '../../types';

type NumberOfAnswersOption = {
  name: string;
  value: string;
};

interface CourseFormProps {
  course?: Course;
  isSubmitting?: boolean;

  onSubmit: (data: CourseFormInput) => void;
}

const CourseForm = (props: CourseFormProps) => {
  const { isSubmitting, course, onSubmit } = props;
  const intl = useIntl();

  const defaultValues = useMemo((): CourseFormInput => {
    if (course) {
      return {
        name: course.name,
        config: course.config,
      };
    } else {
      return {
        name: '',
        config: {
          defaultNumberAnswers: 4,
          defaultIncorrectQuestionType: 'negative',
          exercisesPublicByDefault: false,
          assignmentPositiveAnswerValue: 1,
          assignmentNegativeAnswerValue: -1,
          hideAssignmentSolutionBasedOnScore: false,
          minScoreToShowAssignmenSolution: 0,
          hideSolutionsUntilDueDate: false,
          disablePracticeUntilDueDate: false,

          generalRankingType: 'assignments',
        },
      };
    }
  }, [course]);

  const { handleSubmit, control, formState, watch } = useForm<CourseFormInput>({
    defaultValues,
  });
  const hideAssignmentSolutionBasedOnScore = watch('config.hideAssignmentSolutionBasedOnScore');

  const answerTypes = useMemo(
    () => [
      { name: intl.formatMessage({ id: 'answerType.neutral' }), value: 'neutral' },
      { name: intl.formatMessage({ id: 'answerType.negative' }), value: 'negative' },
    ],
    [intl]
  );

  const numberOfAnswerOptions = useMemo(
    (): NumberOfAnswersOption[] => [
      { name: '2', value: '2' },
      { name: '3', value: '3' },
      { name: '4', value: '4' },
      { name: '5', value: '5' },
    ],
    []
  );

  const generalRankingTypes = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'course.config.rankingType.assignments' }),
        value: 'assignments',
      },
      {
        name: intl.formatMessage({ id: 'course.config.rankingType.questions' }),
        value: 'questions',
      },
    ],
    [intl]
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)} loading={isSubmitting} formState={formState}>
      <FormTextField
        control={control}
        name="name"
        rules={{ required: true }}
        label={intl.formatMessage({ id: 'field.courseName.label' })}
        fullWidth
        type="text"
        sx={{ mb: 2 }}
      />

      <List subheader={<ListSubheader disableGutters>{intl.formatMessage({ id: 'defaultValuesForExercises' })}</ListSubheader>}>
        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.defaultNumberOfAnswerOptions.label' })} />
          <ListItemSecondaryAction>
            <FormSelect
              control={control}
              name="config.defaultNumberAnswers"
              items={numberOfAnswerOptions}
              itemTitleKey="name"
              itemValueKey="value"
              sx={{ pb: 2 }}
            />
          </ListItemSecondaryAction>
        </ListItem>
        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.defaultIncorrectAnswerType.label' })} />
          <ListItemSecondaryAction>
            <FormSelect
              control={control}
              name="config.defaultIncorrectQuestionType"
              items={answerTypes}
              itemTitleKey="name"
              itemValueKey="value"
              sx={{ pb: 2 }}
            />
          </ListItemSecondaryAction>
        </ListItem>
        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.exercisesForPractice.label' })} />
          <ListItemSecondaryAction>
            <FormSwitch control={control} name="config.exercisesPublicByDefault" />
          </ListItemSecondaryAction>
        </ListItem>
      </List>
      <List subheader={<ListSubheader disableGutters>{intl.formatMessage({ id: 'defaultValuesForAssignments' })}</ListSubheader>}>
        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.defaultPositiveAnswerValue.label' })} />
          <ListItemSecondaryAction>
            <FormTextField
              control={control}
              name="config.assignmentPositiveAnswerValue"
              rules={{ required: true }}
              type="number"
              inputProps={{
                min: 0,
                style: { textAlign: 'right' },
              }}
            />
          </ListItemSecondaryAction>
        </ListItem>
        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.defaultNegativeAnswerValue.label' })} />
          <ListItemSecondaryAction>
            <FormTextField
              control={control}
              name="config.assignmentNegativeAnswerValue"
              rules={{ required: true }}
              type="number"
              inputProps={{ max: 0, style: { textAlign: 'right' } }}
            />
          </ListItemSecondaryAction>
        </ListItem>
        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.hideAssignmentSolutionsBasedOnScore.label' })} />
          <ListItemSecondaryAction>
            <FormSwitch control={control} name="config.hideAssignmentSolutionBasedOnScore" />
          </ListItemSecondaryAction>
        </ListItem>
        {hideAssignmentSolutionBasedOnScore && (
          <ListItem disableGutters divider>
            <ListItemText primary={intl.formatMessage({ id: 'field.minScoreToShowAssignmentSolution.label' })} />
            <ListItemSecondaryAction>
              <FormTextField
                control={control}
                name="config.minScoreToShowAssignmenSolution"
                defaultFormValue={0}
                size="small"
                inputProps={{ min: 0, max: 100, style: { textAlign: 'right' } }}
              />
            </ListItemSecondaryAction>
          </ListItem>
        )}

        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.hideSolutionUntilDueDate.label' })} />
          <ListItemSecondaryAction>
            <FormSwitch control={control} name="config.hideSolutionsUntilDueDate" />
          </ListItemSecondaryAction>
        </ListItem>

        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'field.disablePracticeUntilDueDate.label' })} />
          <ListItemSecondaryAction>
            <FormSwitch control={control} name="config.disablePracticeUntilDueDate" />
          </ListItemSecondaryAction>
        </ListItem>
      </List>
      <List subheader={<ListSubheader disableGutters>{intl.formatMessage({ id: 'course.config.ranking' })}</ListSubheader>}>
        <ListItem disableGutters divider>
          <ListItemText primary={intl.formatMessage({ id: 'course.config.rankingType.text' })} />
          <ListItemSecondaryAction>
            <FormSelect
              sx={{ pb: 2 }}
              control={control}
              name="config.generalRankingType"
              items={generalRankingTypes}
              itemTitleKey="name"
              itemValueKey="value"
            />
          </ListItemSecondaryAction>
        </ListItem>
      </List>
    </Form>
  );
};

export default CourseForm;
